import {
	connectToRouter,
	createBrowserNavigationUrl,
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps
} from "client/Navigation";
import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef
} from "react";
import { HubLayoutContext } from "../HubLayout";
import { tryParseInt } from "client/Misc/parseUtils";
import { EventsPageContent, EventsView } from "./EventsPageContent";
import { isNil, isNotNil } from "client/Misc/utils";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubDataSource, HubItemCategoryModel } from "client/Api/Hub/hub";
import { useUnavailableCategoryRedirect } from "../useUnavailableCategoryRedirect";
import { useCategories } from "../useCategories";
import { useCanOpenHubManagement } from "../useCanOpenHubManagement";
import { useForceRerender } from "client/Hooks";
import {
	HubDialogContext,
	HubDialogContextData,
	ViewSpecificHandlers
} from "../common";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import { HubEvent, publish } from "../events";
import { hubRouting } from "../routing";

export type EventsPageProps = {
	id?: number;
	categoryId?: number;
	commentId?: number;
	onNavigate: NavigationCallback;
};

export const EventsPage = (props: EventsPageProps): ReactElement | null => {
	const {
		id,
		categoryId,
		commentId,
		onNavigate
	} = props;

	const {
		hierarchy,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities,
		activeFilter,
		changeActiveFilter,
		changeActiveSidebarItem,
		addOnBeforeNavigateToSidebarItemHandler
	} = useContext(HubLayoutContext);

	const [forceRerender] = useForceRerender();
	const viewSpecificHandlersRef: RefObject<ViewSpecificHandlers | undefined> = useRef<ViewSpecificHandlers>(undefined);

	const categories: OptionalModel<readonly HubItemCategoryModel[]> = useCategories(
		hierarchy,
		HubItemKind.Event
	);

	const canOpenHubManagement: boolean = useCanOpenHubManagement(
		categories,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities
	);

	useUnavailableCategoryRedirect(
		HubItemKind.Event,
		categories,
		categoryId,
		onNavigate
	);

	useEffect(
		(): void => {
			changeActiveSidebarItem({
				source: HubDataSource.Event,
				categoryId
			});
		},
		[categoryId, changeActiveSidebarItem]
	);

	const changeViewHandler = useCallback(
		(view: EventsView): void => {
			const navigation: Navigation = getHubNavigationForEvent({
				source: HubDataSource.Event,
				categoryId: categoryId,
				itemId: view.kind === "details"
					? view.id
					: undefined
			});

			onNavigate(navigation);
		},
		[categoryId, onNavigate]
	);

	const selectCategoryHandler = useCallback(
		async (categoryId: number | null): Promise<void> => {
			if (viewSpecificHandlersRef.current?.beforeSwitchingCategoryHandler) {
				const shouldStay: boolean = await viewSpecificHandlersRef.current
					.beforeSwitchingCategoryHandler();

				if (shouldStay) {
					return;
				}
			}

			const navigation: Navigation = getHubNavigationForEvent({
				source: HubDataSource.Event,
				categoryId: categoryId ?? undefined,
				itemId: undefined
			});

			onNavigate(navigation);
		},
		[onNavigate]
	);

	const categoryCreatedHandler = useCallback(
		(): void => {
			publish(HubEvent.HierarchyChanged, undefined);
		},
		[]
	);

	const hubDialogContext = useMemo(
		(): HubDialogContextData => {
			return {
				setViewSpecificHandlers: (options): void => {
					viewSpecificHandlersRef.current = options;
					forceRerender();
				}
			};
		},
		[forceRerender]
	);

	useEffect(
		(): (() => void) | void => {
			return addOnBeforeNavigateToSidebarItemHandler(
				async (): Promise<boolean> => {
					const { current: viewSpecificHandlers } = viewSpecificHandlersRef;

					if (!viewSpecificHandlers?.beforeSwitchingCategoryHandler) {
						return false;
					}

					return await viewSpecificHandlers.beforeSwitchingCategoryHandler();
				}
			);
		},
		[addOnBeforeNavigateToSidebarItemHandler]
	);

	const backHandler: (() => void) | undefined = viewSpecificHandlersRef.current
		?.backHandler;

	const view: EventsView = isNil(id)
		? { kind: "overview" }
		: {
			kind: "details",
			id,
			commentId
		};

	if (!categories.isSet) {
		return null;
	}

	return (
		<>
			{backHandler && (
				<TitleBarBackButton
					onBack={backHandler}
				/>
			)}

			<HubDialogContext.Provider value={hubDialogContext}>
				<EventsPageContent
					view={view}
					onViewChange={changeViewHandler}
					selectedCategoryId={categoryId ?? null}
					onSelectCategory={selectCategoryHandler}
					categories={categories.value}
					onCategoryCreated={categoryCreatedHandler}
					hubItemKindsCanCreateNewCategoriesFor={hubItemKindsCanCreateNewCategoriesFor}
					activeFilter={activeFilter}
					onActiveFilterChange={changeActiveFilter}
					canOpenHubManagement={canOpenHubManagement}
				/>
			</HubDialogContext.Provider>
		</>
	);
};
EventsPage.displayName = "EventsPage";

export const EventsPageForRouter: FunctionComponent = connectToRouter(
	EventsPage,
	{
		"id": {
			isRequired: false,
			tryParse: tryParseInt
		},
		"categoryId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"commentId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export type EventsPageRouteParams = RouteParametersFromProps<
	EventsPageProps,
	never,
	"id"
>;

export type EventsPageQueryParams = QueryParametersFromProps<
	EventsPageProps,
	"categoryId"
	| "commentId"
>;

export type EventRouterUrlOptions = {
	source: HubDataSource.Event;
	categoryId?: number;
} & ({
	itemId: undefined;
} | {
	itemId: number;
	commentId?: number;
});

export function getHubNavigationForEvent(
	options: EventRouterUrlOptions
): Navigation {
	return hubRouting.events({
		routeParams: {
			id: isNotNil(options.itemId)
				? `${options.itemId}`
				: undefined
		},
		queryParams: {
			categoryId: isNotNil(options.categoryId)
				? `${options.categoryId}`
				: undefined,
			commentId: typeof options.itemId === "number"
				&& isNotNil(options.commentId)
					? `${options.commentId}`
					: undefined
		}
	});
}

export function getHubUrlForEvent(
	options: EventRouterUrlOptions
): string {
	return createBrowserNavigationUrl(
		getHubNavigationForEvent(options)
	);
}
